import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';

// Celebration Animation Components
export const CelebrationAnimation1 = () => (
  <motion.div
    initial={{ opacity: 0, scale: 0.1 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.1 }}
    transition={{ duration: 0.6, ease: "backOut" }}
    style={{
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 1000,
      pointerEvents: 'none'
    }}
  >
    <motion.div
      animate={{ 
        rotate: [0, 5, -5, 0],
        scale: [0.8, 1.1, 1]
      }}
      transition={{ 
        duration: 0.5, 
        repeat: 2,
        ease: "easeInOut",
        delay: 0.2
      }}
      style={{
        background: 'linear-gradient(45deg, #4CAF50, #8BC34A)',
        color: 'white',
        padding: '20px 40px',
        borderRadius: '20px',
        fontSize: '2rem',
        fontWeight: 'bold',
        boxShadow: '0 10px 30px rgba(76, 175, 80, 0.3)',
        textAlign: 'center'
      }}
    >
      🎉 Correct! 🎉
    </motion.div>
  </motion.div>
);

export const CelebrationAnimation2 = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.4 }}
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      zIndex: 1000,
      pointerEvents: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}
  >
    {/* Sparkles */}
    {[...Array(12)].map((_, i) => (
      <motion.div
        key={i}
        initial={{ 
          opacity: 0, 
          scale: 0,
          x: 0,
          y: 0
        }}
        animate={{ 
          opacity: [0, 1, 0], 
          scale: [0, 1, 0],
          x: Math.cos(i * 30 * Math.PI / 180) * 150,
          y: Math.sin(i * 30 * Math.PI / 180) * 150
        }}
        transition={{ 
          duration: 1.2, 
          delay: i * 0.1,
          ease: "easeOut"
        }}
        style={{
          position: 'absolute',
          width: '20px',
          height: '20px',
          background: '#FFD700',
          borderRadius: '50%',
          boxShadow: '0 0 20px #FFD700'
        }}
      />
    ))}
    
    {/* Center text */}
    <motion.div
      initial={{ opacity: 0, scale: 0, rotateY: 0 }}
      animate={{ 
        opacity: 1, 
        scale: [0, 1.2, 1],
        rotateY: 360
      }}
      transition={{ 
        duration: 0.8,
        times: [0, 0.6, 1],
        ease: "backOut"
      }}
      style={{
        background: 'linear-gradient(135deg, #FF6B6B, #4ECDC4)',
        color: 'white',
        padding: '25px 50px',
        borderRadius: '25px',
        fontSize: '2.2rem',
        fontWeight: 'bold',
        boxShadow: '0 15px 40px rgba(0,0,0,0.2)',
        textAlign: 'center'
      }}
    >
      ✨ Amazing! ✨
    </motion.div>
  </motion.div>
);

export const CelebrationAnimation3 = () => {
  // Safe window access for SSR compatibility
  const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const windowHeight = typeof window !== 'undefined' ? window.innerHeight : 800;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1000,
        pointerEvents: 'none'
      }}
    >
      {/* Falling confetti */}
      {[...Array(20)].map((_, i) => (
        <motion.div
          key={i}
          initial={{ 
            opacity: 1,
            y: -20,
            x: Math.random() * windowWidth,
            rotate: 0
          }}
          animate={{ 
            y: windowHeight + 20,
            rotate: 720,
            opacity: [1, 1, 0]
          }}
          transition={{ 
            duration: 2 + Math.random() * 2,
            delay: Math.random() * 0.5,
            ease: "linear"
          }}
          style={{
            position: 'absolute',
            width: '10px',
            height: '10px',
            background: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7'][Math.floor(Math.random() * 5)],
            borderRadius: Math.random() > 0.5 ? '50%' : '0%'
          }}
        />
      ))}
      
      {/* Bouncing text */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      }}>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ 
            opacity: 1, 
            y: [50, -20, 0],
            scale: [0.8, 1.1, 1]
          }}
          transition={{ 
            duration: 0.8,
            times: [0, 0.6, 1],
            ease: "backOut"
          }}
          style={{
            background: 'linear-gradient(45deg, #667eea, #764ba2)',
            color: 'white',
            padding: '20px 45px',
            borderRadius: '30px',
            fontSize: '2rem',
            fontWeight: 'bold',
            boxShadow: '0 20px 50px rgba(102, 126, 234, 0.4)',
            textAlign: 'center'
          }}
        >
          🚀 Excellent! 🚀
        </motion.div>
      </div>
    </motion.div>
  );
};

interface CelebrationRendererProps {
  celebrationAnimation: number | null;
}

export const CelebrationRenderer: React.FC<CelebrationRendererProps> = React.memo(({ celebrationAnimation }) => {
  const renderCelebrationAnimation = () => {
    switch(celebrationAnimation) {
      case 1: return <CelebrationAnimation1 />;
      case 2: return <CelebrationAnimation2 />;
      case 3: return <CelebrationAnimation3 />;
      default: return null;
    }
  };

  if (!celebrationAnimation) return null;

  return createPortal(
    <AnimatePresence mode="wait">
      <div key={`celebration-${celebrationAnimation}`}>
        {renderCelebrationAnimation()}
      </div>
    </AnimatePresence>,
    document.body
  );
});

// Utility function to get a random celebration animation number
export const getRandomCelebrationAnimation = (): number => {
  return Math.floor(Math.random() * 3) + 1;
};
